import { LogOut } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { initialsOf } from '@/lib/auth/session';

/**
 * Who is signed in, and what they are allowed to do.
 *
 * One row: avatar, email, role. The theme toggle used to live here and is now
 * in the top bar, where the design puts it and where it is reachable without
 * opening the drawer on a phone. Sign out stays — it is the only way out of
 * the console, and the design simply did not draw one.
 */
export function SidebarFooter() {
  const { admin, signOut, can } = useAuth();
  const [isSigningOut, setSigningOut] = useState(false);

  const onSignOut = useCallback(() => {
    setSigningOut(true);
    void signOut().finally(() => {
      setSigningOut(false);
    });
  }, [signOut]);

  if (admin === null) return null;

  return (
    <div className="flex shrink-0 items-center gap-2.5 border-t border-sidebar-border px-3.5 py-2.5">
      <span
        aria-hidden="true"
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[11px] leading-none font-semibold text-primary"
      >
        {initialsOf(admin)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-medium text-sidebar-foreground">
          {admin.email}
        </span>
        {/*
          Support operators are read-only server-side. Saying so here is what
          stops someone hunting for a button that was never going to work.
        */}
        <span className="block truncate text-[11.5px] text-sidebar-foreground/60">
          Role: {can.mutate ? 'admin' : 'support, read-only'}
        </span>
      </span>

      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onSignOut}
        disabled={isSigningOut}
        aria-label={isSigningOut ? 'Signing out…' : 'Sign out'}
        title="Sign out"
        className="shrink-0 text-sidebar-foreground/60"
      >
        <LogOut aria-hidden="true" className="size-3.5" />
      </Button>
    </div>
  );
}
